import { action, computed, makeObservable, observable, override, runInAction, toJS } from "mobx";
import { Intent } from "@blueprintjs/core";
import type { ReactNode } from "react";
import debounce from "lodash.debounce";

import {
  SearchFilterKeys,
  SearchFilterProps,
  SearchFilterValue,
  SearchFilters,
  getMicrobeSearchSections,
} from "../utils/search";

import {
  ApiClient,
  CollectionName,
  GenomeInList,
  GenomePublicationChange,
  GenomesBulkDownloadPayload,
  GenomesBulkDownloadQueryResult,
  GenomesOrderBy,
  PaginatedGenomesQuery,
  RestCallResult,
} from "../api";
import { AppToaster } from "../components/toaster";
import { assertUnreachable } from "../utils";
import { logEvent, trackSegmentEvent } from "../utils/analytics";
import { GenomesPageData } from "../utils/genomes";
import UserStore from "./user-store";
import BaseGenomeStore, { observedGenome } from "./base-genome-store";
import { FEATURE_FLAGS } from "../pages/_app";
import { RESTRICTED_COUNTRY_DOWNLOAD_ERROR_MSG } from "../utils/constants";

export type DisplayKind = "table" | "tree";

type SegmentDownloadData = {
  genome_name: string;
  lot_id: string | null;
  context: string;
  format: string;
  genome_uuid?: string;
  assembly_uuid?: string;
  methylation_uuid?: string;
};

function getDefaultData(): GenomesPageData {
  return {
    genomes: observable.array([], { deep: false }),
    loading: false,
    pagination: { page: 1 },
    error: false,
  };
}

export default class GenomeStore extends BaseGenomeStore {
  @observable public data = getDefaultData();
  @observable public hoveredGenome: { uuid: string; color: number } | null = null;

  @observable showUnsequencedInTree = true;
  @observable displayKind: DisplayKind = "table";

  constructor(api: ApiClient, userStore: UserStore, ssrData?: GenomeStore) {
    super(api, userStore, ssrData);
    makeObservable(this);

    if (ssrData) {
      runInAction(() => {
        this.displayKind = ssrData.displayKind;
        this.showUnsequencedInTree = ssrData.showUnsequencedInTree;

        this.data = getDefaultData();
        this.data.genomes.replace(ssrData.data.genomes.map((g) => observedGenome(g)));
        this.data.pagination = ssrData.data.pagination;
        this.data.loading = false;
        this.data.error = ssrData.data.error;
      });
    }
  }

  get searchSections(): SearchFilterProps[] {
    return getMicrobeSearchSections(this.displayingTreeWithShowUnsequenced);
  }

  get pageNum(): number | undefined {
    return this.data.pagination.page;
  }

  get defaultCollectionNames(): CollectionName[] {
    return ["bacteriology", "mycology", "protistology", "virology"];
  }

  @action
  updateIfNotHydrated = (
    collectionName: CollectionName | null,
    params: { [key: string]: unknown }
  ) => {
    if (this.hydrated) {
      // Data already updated via SSR. But next time we will need to fetch it.
      // So marking itself as non-hydrated.
      this.hydrated = false;
    } else {
      this.setVariablesFromQuery(params);
      this.setCollectionName(collectionName);
      this.fetchGenomes();
      this.selectAllActivated = false;
      this.selectionUuids.clear();
    }
  };

  // Only set in the all page
  @action
  setVariablesFromQuery = (params: { [key: string]: unknown }) => {
    if (params["related_genome"]) {
      this.searchObject.related_genome = params["related_genome"] as string;
    }

    this.baseSetVariablesFromQuery(params);

    const page = params["page"] as string;
    const displayKind = params["display"] as string;
    const showUnsequencedInTree = params["unsequenced"] as string;

    this.showUnsequencedInTree = showUnsequencedInTree !== "false";
    this.data.pagination.page = page ? parseInt(page, 10) : 1;
    if (FEATURE_FLAGS.CatalogTree) {
      this.displayKind = displayKind === "tree" ? "tree" : "table";
    } else {
      this.displayKind = "table";
    }
  };

  @action
  fetchGenomes = async (): Promise<void> => {
    const page = this.data.pagination.page || 1;
    const payload = this.getFetchGenomesPayload();

    this.data.loading = true;
    this.hoveredGenome = null;

    const result = await this.api.post<GenomeInList[]>(
      "/genome-portal-api/genomes",
      payload,
      {},
      { page: page }
    );

    runInAction(() => {
      this.data.loading = false;

      if (result.error !== undefined) {
        this.data.error = true;
        this.data.genomes.clear();
      } else {
        this.data.error = false;
        this.data.genomes.replace(result.data.map((g) => observedGenome(g)));
        this.data.pagination = JSON.parse(result.headers.get("X-Pagination")!);
      }
    });
    this.deselectAll();
  };

  getFetchGenomesPayload = (): Omit<PaginatedGenomesQuery, "search_object"> & {
    search_object: SearchFilters;
  } => {
    const collection_name: CollectionName[] = this.collectionName
      ? [this.collectionName]
      : this.defaultCollectionNames;
    return {
      order_by: this.orderBy,
      ascending: this.ascending,
      collection_name,
      search_object: toJS(this.searchObject),
      mine_only: this.mineOnly,
      show_unsequenced: this.showUnsequencedInTree,
    };
  };

  private debouncedFetchGenomes = debounce(this.fetchGenomes, 250);

  fetchHistory = async (uuid: string): Promise<GenomePublicationChange[]> => {
    const resp = await this.api.get<GenomePublicationChange[]>(
      "/genome-portal-api/genome-history/{uuid}",
      { uuid }
    );
    if (resp.error === undefined) {
      return resp.data;
    }
    return [];
  };

  @action
  setHoveredGenome = (hoveredGenome: { uuid: string; color: number } | null) => {
    this.hoveredGenome = hoveredGenome;
  };

  @action
  setOrderBy = (orderBy: GenomesOrderBy) => {
    this.baseSetOrderBy(orderBy);
    this.fetchGenomes();
  };

  @override
  removeSearchFilter(key: SearchFilterKeys, val: SearchFilterValue): void {
    super.removeSearchFilter(key, val);

    if (
      (key === "sequence" && this.orderBy === "search_similarity") ||
      (key === "related_genome" && this.orderBy === "genome_similarity")
    ) {
      // The column we're sorting by will no longer exist. Fall back to the default sort.
      this.orderBy = this.defaultOrderBy;
      this.ascending = this.defaultAscending(this.orderBy);
    }

    this.data.pagination.page = 1;
    this.data.genomes.clear();
    this.fetchGenomes();
  }

  @override
  addSearchFilter<K extends keyof SearchFilters>(key: K, val: string): void {
    super.addSearchFilter(key, val);

    if (key === "sequence") {
      if (val) {
        this.orderBy = "search_similarity";
        this.ascending = this.defaultAscending(this.orderBy);
      } else if (this.orderBy === "search_similarity") {
        // The column we're sorting by will no longer exist. Fall back to the default sort.
        this.orderBy = this.defaultOrderBy;
        this.ascending = this.defaultAscending(this.orderBy);
      }
    }

    if (key === "related_genome") {
      this.orderBy = "genome_similarity";
      this.ascending = this.defaultAscending(this.orderBy);
    }
    this.data.pagination.page = 1;
    this.data.genomes.clear();
    this.data.loading = true;
    this.debouncedFetchGenomes();
  }

  @computed
  get sequenceSearchFilter(): SearchFilterProps | null {
    return this.inUseSearchFields.find((field) => field.key === "sequence") || null;
  }

  @computed
  get sequenceSearchValue(): string {
    return this.searchObject.sequence || "";
  }

  @computed
  get sequenceSearchEnabled(): boolean {
    return this.sequenceSearchValue.length > 0;
  }

  @computed
  get relatedGenomeSearchEnabled(): boolean {
    return !!this.searchObject.related_genome;
  }

  @action
  setPage = (page: number) => {
    this.data.pagination.page = page;
    this.fetchGenomes();
  };

  @action
  setShowUnsequencedInTree = (newValue: boolean) => {
    // this.searchSections will depend on new this.showUnsequencedInTree so
    // it is important to set it first.
    this.showUnsequencedInTree = newValue;

    if (this.showUnsequencedInTree) {
      this.updateSearchObjectOnSearchSectionsChanged();
    }
  };

  @action
  setDisplayKind = (newDisplayKind: DisplayKind) => {
    // this.searchSections will depend on new this.displayKind so
    // it is important to set it first.
    const prevDisplayKind = this.displayKind;
    this.displayKind = newDisplayKind;

    if (prevDisplayKind !== newDisplayKind && newDisplayKind === "tree") {
      if (this.countDisabledSearchSectionsWithSearchParams()) {
        this.showUnsequencedInTree = false;
      } else {
        this.updateSearchObjectOnSearchSectionsChanged();
      }
    }
  };

  getNumSelected = (): { numSelected: number; numTotal: number } => {
    const numTotal = this.data.pagination.total ?? this.data.genomes.length;
    const selectedSize = this.selectionUuids.size;
    if (this.selectAllActivated) {
      return { numSelected: numTotal - selectedSize, numTotal };
    }
    return { numSelected: selectedSize, numTotal };
  };

  initBulkDownload = async (
    downloadTypes: GenomesBulkDownloadPayload["download_types"]
  ): Promise<RestCallResult<unknown>> => {
    const fetchPayload = this.getFetchGenomesPayload();
    return await this.baseInitBulkDownload(downloadTypes, {
      show_unsequenced: fetchPayload.show_unsequenced,
    });
  };

  initBulkSearch = async (
    downloadTypes: GenomesBulkDownloadPayload["download_types"]
  ): Promise<RestCallResult<GenomesBulkDownloadQueryResult>> => {
    const fetchPayload = this.getFetchGenomesPayload();
    return await this.baseSearchBulkDownload(downloadTypes, {
      show_unsequenced: fetchPayload.show_unsequenced,
    });
  };

  formatGenomeName(genomeName: string, searchTerm: string): string {
    let filename = genomeName.replace(" ", "_").replace("®", "").replace("™", "");
    if (searchTerm) {
      filename = filename + "_" + searchTerm;
    }
    return filename;
  }

  async downloadAnnotationsCSV(
    genomeName: string,
    searchTerm: string,
    annotationUuids: string[],
    userEmail: string | null,
    genomeUuid?: string,
    assemblyUuid?: string
  ): Promise<ReactNode> /* error msg or null */ {
    const formattedGenomeName = this.formatGenomeName(genomeName, searchTerm);
    logEvent("annotations", "download", genomeName);
    const segmentData: SegmentDownloadData = {
      genome_name: formattedGenomeName,
      assembly_uuid: assemblyUuid,
      genome_uuid: genomeUuid,
      lot_id: this.userStore.lotId ? this.userStore.lotId : null,
      context: "Annotations Viewer",
      format: "csv",
    };
    trackSegmentEvent("Download Annotations", segmentData);

    const response = await this.api.post<unknown>("/genome-portal-api/annotations-csv-export", {
      uuids: annotationUuids,
      filename: formattedGenomeName,
    });

    if (response.error === undefined) {
      const msg =
        userEmail !== null
          ? `We are preparing your download. We will email a link to you (${userEmail}) once your download is ready. Time to generate downloads may vary, depending on number of genomes selected and file sizes.`
          : "We are preparing your download. We will email a link to you once your download is ready. Time to generate downloads may vary, depending on number of genomes selected and file sizes.";
      AppToaster!.show({
        message: msg,
        intent: Intent.PRIMARY,
        timeout: 0, // disabled
      });
      return null;
    } else if (response.statusCode === 451) {
      return RESTRICTED_COUNTRY_DOWNLOAD_ERROR_MSG;
    } else {
      return `Could not download the annotations, please try again later.`;
    }
  }

  @action
  setCollectionName = (collectionName: CollectionName | null) => {
    this.collectionName = collectionName;
  };

  protected setGenomeStarring(uuid: string, starred: boolean) {
    for (const genome of this.data.genomes) {
      if (genome.uuid === uuid) {
        genome.starred = starred;
        break;
      }
    }
  }

  hoveredGenomeHighlightedBases = (): boolean[] => {
    if (this.hoveredGenome === null) {
      return this.allHighlightedBases();
    }

    const flags: boolean[] = Array.from<boolean>({ length: this.sequenceSearchValue.length }).fill(
      false
    );
    for (const genome of this.data.genomes) {
      if (genome.uuid === this.hoveredGenome.uuid) {
        for (const position of genome.primary_assembly?.matching_bases || []) {
          flags[position] = true;
        }
        break;
      }
    }
    return flags;
  };

  allHighlightedBases = (): boolean[] => {
    const flags: boolean[] = Array.from<boolean>({ length: this.sequenceSearchValue.length }).fill(
      false
    );
    for (const genome of this.data.genomes) {
      for (const position of genome.primary_assembly?.matching_bases || []) {
        flags[position] = true;
      }
    }
    return flags;
  };

  get defaultOrderBy(): GenomesOrderBy {
    return this.relatedGenomeSearchEnabled ? "genome_similarity" : super.defaultOrderBy;
  }

  get descendingFirstColumns(): GenomesOrderBy[] {
    return ["created_at", "search_similarity", "genome_similarity"];
  }

  get displayingTreeWithShowUnsequenced(): boolean {
    return this.displayKind === "tree" && this.showUnsequencedInTree;
  }

  protected addAdditionalUrlParams(params: URLSearchParams): void {
    if (this.displayKind !== "table") {
      params.set("display", "tree");
    }
    if (!this.showUnsequencedInTree) {
      params.set("unsequenced", "false");
    }
  }

  protected updateSearchObjectOnSearchSectionsChanged(): void {
    for (const section of this.searchSections) {
      if (!section.disabled) {
        continue;
      }
      switch (section.key) {
        case "tags":
          this.searchObject.tags.clear();
          break;
        case "isolation":
        case "type_strain":
        case "bsl":
        case "taxon_id":
        case "text":
        case "catalog_number":
        case "strain_name":
        case "taxonomy":
        case "gene":
        case "sequence":
        case "related_genome":
        case "cell_line_name":
        case "tissue":
        case "disease":
          this.searchObject[section.key] = null;
          break;
        default:
          assertUnreachable(section.key);
      }
    }
  }

  protected countDisabledSearchSectionsWithSearchParams(): number {
    let result = 0;

    for (const section of this.searchSections) {
      if (!section.disabled) {
        continue;
      }

      if (section.key === "tags") {
        result += this.searchObject.tags.length > 0 ? 1 : 0;
      } else {
        result += this.searchObject[section.key] ? 1 : 0;
      }
    }

    return result;
  }
}
